import { useRouter } from 'next/router';
import { ReactNode, useCallback } from 'react';
import { FormProvider, SubmitHandler, UseFormHandleSubmit, useForm } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import { TriggerCondition } from '../../hooks/adminPage/triggers/useTriggers';
import { createTrigger } from '../../services/admin/triggers';
import { grispiNotification } from '../../store/useNotificationStore';
import { parseMultipleUserIdFromUserValue, parseUserIdFromUserValue } from '../../utils/forms/formHelper.parser';
import requestWithMessage from '../../utils/requestWithMessage';
import { pushTo } from '../../utils/routeHelper';
import { CustomFieldType } from '../CustomField/CustomField.type';

export type IEmailToUserValueType = {
    to: string;
    subject: string;
    body: string;
};
export type ICopyValueType = {
    targetKey: string;
    sourceKey: string;
};
export type IDateValueType = {
    operator: string;
    dateValue: string;
};
export type IAssigneeValueType = {
    operator: string;
    assigneeValue: string;
};
export interface IConditionTriggerForm extends Pick<TriggerCondition, 'name' | 'cfKey'> {
    operator: TriggerCondition['operator'];
    value: string | IEmailToUserValueType | ICopyValueType | IDateValueType | IAssigneeValueType;
    key: string;
    type: CustomFieldType | 'CUSTOM_FIELD' | 'EMAIL_USER' | 'COPY_VALUE';
}

export enum TriggerDateActionOperatorMap {
    SELECT_DATE = 'SELECT_DATE',
    YESTERDAY = 'YESTERDAY',
    NOW = 'NOW',
    TODAY = 'TODAY',
    TOMORROW = 'TOMORROW',
    NEXT_WEEK = 'NEXT_WEEK',
    NEXT_MONTH = 'NEXT_MONTH',
    NEXT_YEAR = 'NEXT_YEAR'
}
export enum TriggerAssigneeActionOperatorMap {
    SELECT_ASSIGNEE = 'SELECT_ASSIGNEE',
    AUTO_ASSIGN_TICKET_TO_AGENT = 'AUTO_ASSIGN_TICKET_TO_AGENT'
}
export type IActionTriggerForm = Omit<IConditionTriggerForm, 'operator'>;

export type CreateTriggerFormValues = {
    active: boolean;
    actions: IActionTriggerForm[];
    allConditions: IConditionTriggerForm[];
    anyConditions: IConditionTriggerForm[];
    category: string; // TODO: trigger, should be selectable with available options
    description: string;
    name: string;
};

const changeTriggerActionValueToDateValue = (value: IDateValueType) => {
    if (!value) return value;
    if (!value.operator) return value;
    if (value.operator !== TriggerDateActionOperatorMap.SELECT_DATE) {
        return value.operator;
    }
    return value.dateValue;
};
const changeTriggerActionValueToAssigneeValue = (value: IAssigneeValueType) => {
    if (!value) return value;
    if (!value.operator) return value;
    if (value.operator !== TriggerAssigneeActionOperatorMap.SELECT_ASSIGNEE) {
        return value.operator.concat(':', value.assigneeValue);
    }
    return value.assigneeValue;
};

const defaultValues: CreateTriggerFormValues = {
    actions: [],
    active: true,
    allConditions: [],
    anyConditions: [],
    category: 'Default',
    description: '',
    name: ''
};

export const allBuiltinUserValuesForActions = [':assignee', ':creator', ':requester', ':currentUser', ':notAssigned'];

const emailToUserBuiltinUserValues = [':assignee', ':currentUser', ':requester'];
const updatedByBuiltinUserValues = [':assignee', ':creator', ':requester'];

const conditionValueGenerator = (condition: IConditionTriggerForm | IActionTriggerForm) => {
    // TODO: if user and multi user type custom fields are need userId instead of serialized value, uncomment below code
    if (isCustomFieldConditionNew(condition))
        switch (condition.type) {
            case 'USER':
                return !allBuiltinUserValuesForActions.includes(condition.value as string)
                    ? parseUserIdFromUserValue(condition.value as string)
                    : condition.value;
            case 'MULTI_USER': // should send options id instead of value
                return parseMultipleUserIdFromUserValue(condition.value as string);
            case 'DATE_TIME':
                return changeTriggerActionValueToDateValue(condition.value as IDateValueType);
            case 'DATE':
                return changeTriggerActionValueToDateValue(condition.value as IDateValueType);
            default:
                return condition.value;
        }
    else
        switch (condition.key) {
            case 'EMAIL_USER':
                return !emailToUserBuiltinUserValues.includes((condition.value as IEmailToUserValueType).to)
                    ? {
                          ...(condition.value as IEmailToUserValueType),
                          to: parseUserIdFromUserValue((condition.value as IEmailToUserValueType).to?.toString())
                      }
                    : condition.value;
            case 'UPDATED_BY':
                return !updatedByBuiltinUserValues.includes(condition.value as string)
                    ? parseUserIdFromUserValue(condition.value as string)
                    : condition.value;
            case 'REQUESTER':
                return parseUserIdFromUserValue(condition.value as string);
            case 'CURRENT_USER':
                return parseUserIdFromUserValue(condition.value as string);
            case 'ADD_FOLLOWER':
                return parseMultipleUserIdFromUserValue(condition.value as string);
            case 'ASSIGNEE':
                return changeTriggerActionValueToAssigneeValue(condition.value as IAssigneeValueType);
            default:
                return condition.value;
        }
};
const CUSTOM_FIELD_CONDITION_TYPE = 'CUSTOM_FIELD';

const isCustomFieldConditionNew: (condition: IConditionTriggerForm | IActionTriggerForm) => boolean = (
    condition: IConditionTriggerForm | IActionTriggerForm
) => !!condition?.cfKey;

export const prepareConditionForApi = <T extends IConditionTriggerForm | IActionTriggerForm>(condition: T): T =>
    isCustomFieldConditionNew(condition)
        ? {
              ...condition,
              type: CUSTOM_FIELD_CONDITION_TYPE,
              value: conditionValueGenerator(condition)
          }
        : {
              ...condition,
              type: condition.key,
              value: conditionValueGenerator(condition)
          };

const NewTriggerForm = ({ children }: { children: ReactNode }) => {
    const { t } = useTranslation('admin');
    const { asPath } = useRouter();

    const methods = useForm<CreateTriggerFormValues>({ mode: 'onChange', defaultValues });

    const { getValues, handleSubmit, reset } = methods;

    const handleOnBack = useCallback(() => {
        pushTo(asPath.replaceAll('/new', ''));
    }, [asPath]);

    const onValid: SubmitHandler<CreateTriggerFormValues> = async (formValues) => {
        const startMessage = t('triggers.message.creatingTrigger');
        const finishMessage = t('triggers.message.onCreate');

        const postData: CreateTriggerFormValues = {
            ...formValues,
            description: formValues.description === '' ? ' ' : formValues.description, //backend not except empty string
            actions: formValues.actions.map(prepareConditionForApi),
            allConditions: formValues.allConditions.map(prepareConditionForApi),
            anyConditions: formValues.anyConditions.map(prepareConditionForApi)
        };
        await requestWithMessage({
            key: 'create-trigger',
            startMessage,
            finishMessage,
            apiRequest: async () => {
                await createTrigger(postData);
                handleOnBack();
            }
        });
    };

    const onSubmit = useCallback(
        (handleOnSubmit: ReturnType<UseFormHandleSubmit<CreateTriggerFormValues>>) => (e?: React.BaseSyntheticEvent) =>
            handleOnSubmit(e).catch((error) => {
                if (error.name === 'AxiosError') {
                    // to convert isSubmitting value to false without any data lost
                    reset(getValues(), {
                        keepDirtyValues: true,
                        keepErrors: true,
                        keepDirty: true,
                        keepValues: true,
                        keepDefaultValues: true,
                        keepIsSubmitted: true,
                        keepTouched: true,
                        keepIsValid: true,
                        keepSubmitCount: true
                    });
                    return;
                }
                grispiNotification('error', error?.response?.data?.message ?? error?.message, 'error');
            }),
        [getValues, reset]
    );

    return (
        <FormProvider {...methods}>
            <form onSubmit={onSubmit(handleSubmit(onValid))} className="full-height">
                {children}
            </form>
        </FormProvider>
    );
};

export default NewTriggerForm;
